import styled from "@emotion/styled";
import React from "react";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import { CareMomentRank, CareMomentRankChange } from "../CareMoments.types";
import CareMomentRankIcon from "./CareMomentRankIcon";
import RoadProgress from "./RoadProgress";

export default function RankRoad(props: {
  scaffoldRef: React.RefObject<HTMLDivElement>;
  ranks: CareMomentRank[];
  rankChange: CareMomentRankChange;
  avatarUrl: string | null;
  onFinishAnimation: () => void;
}) {
  const currentRankIndex = props.ranks.findIndex((rank) => rank.id === props.rankChange.to.rank.id);

  return (
    <Wrapper>
      <StackChild>
        <LabelsContainer>
          {props.ranks.map((rank, index) => (
            <LabelContainer key={index}>
              <RankInfo>
                <h4>{rank.title}</h4>
                <p>
                  {currentRankIndex >= index
                    ? translate(STRINGS.RANK_ACHIEVED)
                    : translate(STRINGS.NUM_SHIFTS_TO_GO).replace(
                        "{numShifts}",
                        (
                          rank.requiredPoints -
                          props.rankChange.to.rank.requiredPoints -
                          props.rankChange.to.points
                        ).toString()
                      )}
                </p>
              </RankInfo>
              <RankWageContainer>
                <div>
                  <span>$</span>
                  {rank.wage.toFixed(2)}
                </div>
                <div>
                  <span>{translate(STRINGS.PER_HOUR)}</span>
                </div>
                <RankIconWrapper>
                  <CareMomentRankIcon icon={rank.icon} size={60} />
                </RankIconWrapper>
              </RankWageContainer>
            </LabelContainer>
          ))}
        </LabelsContainer>
      </StackChild>
      <StackChild>
        <RoadProgress
          scaffoldRef={props.scaffoldRef}
          numSegments={props.ranks.length + 1}
          rankChange={props.rankChange}
          ranks={props.ranks}
          avatarUrl={props.avatarUrl}
          onFinishAnimation={props.onFinishAnimation}
        />
      </StackChild>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
`;

const StackChild = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  overflow-x: hidden;
`;

const LabelContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: end;
  height: 84px;
  align-items: center;
`;

const RankInfo = styled.div`
  display: flex;
  flex-direction: column;
  color: var(--tw-blue-900);

  h4 {
    font-size: 16px;
    font-weight: 500;
    margin: 0;
  }

  p {
    font-size: 14px;
    font-weight: 300;
    margin: 0;
  }
`;

const RankWageContainer = styled.div`
  --horizontal-padding: 16px;
  --horizontal-padding-icon: 46px;

  position: relative;
  display: flex;
  flex-direction: column;
  padding: 10px var(--horizontal-padding-icon) 10px var(--horizontal-padding);
  border-radius: 6px;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.2);
  color: var(--blue-gray);
  font-size: 18px;
  font-weight: 700;
  line-height: 14px;

  span {
    font-size: 14px;
    font-weight: 300;
  }
`;

const RankIconWrapper = styled.div`
  --icon-horizontal-offset: -30px;

  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 50%;
  right: var(--icon-horizontal-offset);
  transform: translateY(-50%);
`;

const LabelsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: end;
  margin-top: 33px;
  gap: 16px;

  --avatar-horizontal-offset: 72px;
  --horizontal-padding: 16px;

  ${LabelContainer}:nth-of-type(even) {
    justify-content: start;
    margin-inline-start: var(--avatar-horizontal-offset);
    flex-direction: row-reverse;

    ${RankInfo} {
      padding-inline-end: var(--horizontal-padding);
    }

    ${RankWageContainer} {
      padding-right: var(--horizontal-padding);
      padding-left: var(--horizontal-padding-icon);

      ${RankIconWrapper} {
        left: var(--icon-horizontal-offset);
        right: auto;
      }
    }
  }

  ${LabelContainer}:nth-of-type(odd) {
    margin-inline-end: var(--avatar-horizontal-offset);

    ${RankInfo} {
      padding-inline-start: var(--horizontal-padding);
      text-align: end;
    }
  }
`;
